import { createClient } from '@supabase/supabase-js';
import { Database } from './database.types';
import { observable } from '@legendapp/state';
import { syncedSupabase } from '@legendapp/state/sync-plugins/supabase';
import 'react-native-get-random-values';
import { uuidv7 } from 'uuidv7';
import { configureSynced } from '@legendapp/state/sync';
import { observablePersistAsyncStorage } from '@legendapp/state/persist-plugins/async-storage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { currentUser$ } from './Firebase';

const supabase = createClient<Database>(
  process.env.EXPO_PUBLIC_SUPABASE_URL,
  process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY
);

export function getSupabaseClient() {
  const user = currentUser$.get();
  if (user) {
    return createClient<Database>(
      process.env.EXPO_PUBLIC_SUPABASE_URL,
      process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY,
      {
        global: {
          headers: {
            'x-user-id': user.uid,
          },
        },
      }
    );
  }
  return supabase;
}

// Provide a function to generate ids locally
const generateId = () => uuidv7();

// Create a configured sync function
const customSynced = configureSynced(syncedSupabase, {
  // Use React Native Async Storage
  persist: {
    plugin: observablePersistAsyncStorage({
      AsyncStorage,
    }),
  },
  generateId,
  supabase: getSupabaseClient(),
  changesSince: 'last-sync',
  fieldCreatedAt: 'created_at',
  fieldUpdatedAt: 'updated_at',
  // Optionally enable soft deletes
  fieldDeleted: 'deleted',
});



export const characters$ = observable(
  customSynced({
    collection: 'characters',
    actions: ['read'],
    realtime: true,
    // persist: {
    //  name: 'characters',
    //  retrySync: true,
    // },
  })
);

export async function getOrCreateSession(characterId: string): Promise<string | null> {
  const client = getSupabaseClient();
  const user = currentUser$.get();
  
  if (!user) {
    console.error('[Supa] No user authenticated');
    return null;
  }

  try {
    // Try to find existing session
    const { data: existingSessions, error: fetchError } = await client
      .from('chat_sessions')
      .select('id')
      .eq('character_id', characterId)
      .eq('user_id', user.uid)
      .order('created_at', { ascending: false })
      .limit(1);

    if (fetchError) {
      console.error('[Supa] Error fetching session:', fetchError);
      return null;
    }

    if (existingSessions && existingSessions.length > 0) {
      return existingSessions[0].id;
    }

    // Create new session
    const { data: newSession, error: createError } = await client
      .from('chat_sessions')
      .insert({
        character_id: characterId,
        user_id: user.uid,
      })
      .select('id')
      .single();

    if (createError) {
      console.error('[Supa] Error creating session:', createError);
      return null;
    }

    return newSession.id;
  } catch (error) {
    console.error('[Supa] Error in getOrCreateSession:', error);
    return null;
  }
}

export async function getMessagesForSession(sessionId: string): Promise<Message[]> {
  const client = getSupabaseClient();

  try {
    const { data, error } = await client
      .from('messages')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[Supa] Error fetching messages:', error);
      return [];
    }

    const messages = (data || []).map((row: any) => ({
      id: row.id,
      session_id: row.session_id,
      role: row.role,
      content: row.content,
      imageUrl: row.image_url,
      created_at: row.created_at,
    })) as Message[];

    // Deduplicate: remove consecutive messages with same role + content (legacy duplicates)
    const deduped: Message[] = [];
    for (const msg of messages) {
      const last = deduped[deduped.length - 1];
      if (!last || last.role !== msg.role || last.content !== msg.content) {
        deduped.push(msg);
      }
    }
    return deduped;
  } catch (error) {
    console.error('[Supa] Error in getMessagesForSession:', error);
    return [];
  }
}

interface Message {
  id: string;
  session_id: string;
  role: 'user' | 'assistant';
  content: string;
  imageUrl?: string | null;
  created_at: string;
}
